import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {MatFormField} from '@angular/material/form-field';
import {MatInput, MatLabel} from '@angular/material/input';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {CondominiumListService} from '../../control/condominium-list-service';
import {Condominium} from '../../entity/condominium';
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from '@angular/material/autocomplete';
import {MatSelect} from '@angular/material/select';
import {LocalizationPipePipe} from '../../../localization/control/localisation-pipe-pipe';

export type PropertyNameOption = {
  name: string;
  value: string;
}

@Component({
  selector: 'app-claim-search-form',
  imports: [
    MatFormField,
    MatLabel,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatInput,
    MatSelect,
    LocalizationPipePipe
  ],
  templateUrl: './claim-search-form.html',
  styleUrl: './claim-search-form.scss'
})
export class ClaimSearchForm implements OnInit {
  @Output() onInsuranceClaimReferenceSearch = new EventEmitter<string>()
  @Output() onInsuranceClaimReferenceCancelSearch = new EventEmitter<void>()
  protected isPropertyNameValid: boolean = false;
  protected isPropertyValueValid: boolean = false;
  protected propertyNameValueOptions: PropertyNameOption[] = [
    {name: 'Condominium Name', value: 'condominium'},
    {name: 'Insurance Claim Reference', value: 'insuranceClaimReference'},
    {name: 'Loss Adjuster Claim Reference', value: 'lossAdjusterClaimReference'}
  ];
  protected propertyValueOptions: string[] = [];
  protected filteredPropertyValueOptions: Set<string> = new Set<string>();
  protected propertyNameFormControl: FormControl = new FormControl(
    this.propertyNameValueOptions[0].value,
    {
      nonNullable: true,
      updateOn: "change"
    }
  );
  protected propertyValueFormControl: FormControl = new FormControl(
    null,
    {
      updateOn: "change"
    }
  );

  protected get propertyName(): PropertyNameOption {
    return this.propertyNameValueOptions.find(p => p.value == this.propertyNameFormControl.value) ?? this.propertyNameValueOptions[0];
  }

  constructor(private readonly condominiumListService: CondominiumListService) {
  }

  ngOnInit(): void {
    this.propertyNameFormControl.valueChanges.subscribe(newValue => {
      this.handlePropertyNameChange(newValue)
    })
    this.propertyValueFormControl.valueChanges.subscribe(newValue => {
      this.handlePropertyValueChange(newValue)
    })
    this.handlePropertyNameChange(this.propertyNameValueOptions[0].value)
  }

  private handlePropertyNameChange(newValue: string) {
    let propertyNames = this.propertyNameValueOptions.map(e => e.value);
    if (!propertyNames.includes(newValue)) {
      this.isPropertyNameValid = false;
      this.isPropertyValueValid = false;
      return;
    }

    this.isPropertyNameValid = true;
    this.loadValueOptions(newValue).then(_ => {
      this.propertyValueFormControl.setValue(null);
    })
  }

  private async loadValueOptions(propertyName: string): Promise<void> {
    const cl = await this.condominiumListService.getCondominiumList();

    this.propertyValueOptions = (
      cl?.filter(a => a != null)
        .map(c => (c[propertyName as keyof Condominium]).toString())
    ) ?? [];
    const s = new Set(this.propertyValueOptions)
    this.filteredPropertyValueOptions = new Set([...this.propertyValueOptions])
  }

  private handlePropertyValueChange(value: string) {
    const filteredPropertyValueOptions = value ? this._filter(value as string) : this.propertyValueOptions.slice();
    this.filteredPropertyValueOptions = new Set(filteredPropertyValueOptions);
    if (this.propertyName.value == 'insuranceClaimReference') {
      this.isPropertyValueValid = !isNaN(parseInt(value)) || `${value}`.trim().length > 2;
    } else {
      this.isPropertyValueValid = this.propertyValueOptions.includes(value)
    }
  }

  private _filter(name: string): string[] {
    const filterValue = name.toLowerCase();
    return this.propertyValueOptions.filter(option => {
      return option.toLowerCase().includes(filterValue);
    });
  }

  protected search(): void {
    const propertyName = this.propertyNameFormControl.value
    if (propertyName == 'insuranceClaimReference') {
      this.onInsuranceClaimReferenceSearch.emit(this.propertyValueFormControl.value.toString())
    } else {
      this.condominiumListService.getCondominiumList().then((condos: Condominium[]) => {
        const condo = condos.find(c => {
          return (c[propertyName as keyof Condominium]).toString() === this.propertyValueFormControl.value;
        });
        if (condo && condo.insuranceClaimReference) {
          this.onInsuranceClaimReferenceSearch.emit(condo.insuranceClaimReference.toString())
        }
      })
    }
  }

  cancelSearch() {
    this.propertyNameFormControl.reset();
    this.propertyValueFormControl.reset();
    this.onInsuranceClaimReferenceCancelSearch.emit();
  }
}
